import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Branding
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo29() {
  const pdfRef = useRef();

  // 📊 Dataset
  const data = [
    { ageGroup: "14–18", allPercentage: 1.41 },
    { ageGroup: "19–21", allPercentage: 3.79 },
    { ageGroup: "22–24", allPercentage: 5.8 },
    { ageGroup: "25–34", allPercentage: 22.7 },
    { ageGroup: "35–44", allPercentage: 22.96 },
    { ageGroup: "45–54", allPercentage: 21.06 },
    { ageGroup: "55–64", allPercentage: 16.05 },
    { ageGroup: "65–99", allPercentage: 6.25 },
  ];

  // 📈 Insights
  const avg = (
    data.reduce((sum, d) => sum + d.allPercentage, 0) / data.length
  ).toFixed(2);
  const topGroup = data.reduce((max, d) =>
    d.allPercentage > max.allPercentage ? d : max
  );
  const lowGroup = data.reduce((min, d) =>
    d.allPercentage < min.allPercentage ? d : min
  );

  // Color palette per age group
  const colors = [
    "#FFA1B5", // 14–18
    "#86C7F3", // 19–21
    "#FFE29A", // 22–24
    "#93D9D9", // 25–34
    "#C2A3FF", // 35–44
    "#FFC58C", // 45–54
    "#B9D2AA", // 55–64
    "#6794D1", // 65–99
  ];

  const chartData = {
    labels: data.map((d) => d.ageGroup),
    datasets: [
      {
        label: "Workforce Share (%)",
        data: data.map((d) => d.allPercentage),
        backgroundColor: colors,
        borderRadius: 6,
      },
    ],
  };

  // ⚙️ Chart Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: "#111827", font: { size: 13, weight: "bold" } },
      },
      title: {
        display: true,
        text: "📊 Overall Workforce Distribution by Age Group (2024)",
        color: "#111827",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.y.toFixed(2)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => v + "%", color: "#4B5563" },
        title: {
          display: true,
          text: "Workforce Share (%)",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Age Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // 🧾 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("overall-workforce-by-age-group.pdf");
      // input.style.width = originalWidth;
    });
  };

  // 💅 UI
  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-rose-50 via-white to-amber-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Overall Workforce Distribution (2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Percentage</p>
            <p className="text-xl font-bold text-red-700">
              {topGroup.ageGroup} ({topGroup.allPercentage.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Lowest Percentage</p>
            <p className="text-xl font-bold text-yellow-700">
              {lowGroup.ageGroup} ({lowGroup.allPercentage.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Average Workforce Share</p>
            <p className="text-xl font-bold text-green-700">{avg}%</p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="w-full border-collapse text-center">
              <thead className="bg-red-700 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Workforce Share (%)</th>
                  <th className="p-2">Deviation from Avg</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const deviation = (row.allPercentage - avg).toFixed(2);
                  const isTop = row.ageGroup === topGroup.ageGroup;
                  const isLow = row.ageGroup === lowGroup.ageGroup;
                  return (
                    <tr
                      key={i}
                      className={`even:bg-gray-50 odd:bg-white hover:bg-gray-100 ${
                        isTop
                          ? "bg-green-100 font-semibold"
                          : isLow
                            ? "bg-yellow-100 font-semibold"
                            : ""
                      }`}
                    >
                      <td className="p-2">{row.ageGroup}</td>
                      <td className="p-2 text-red-700">
                        {row.allPercentage.toFixed(2)}%
                      </td>
                      <td
                        className={`p-2 ${
                          deviation > 0 ? "text-green-700" : "text-gray-600"
                        }`}
                      >
                        {deviation > 0 ? "+" : ""}
                        {deviation}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="text-center mt-4 text-gray-700">
          <p className="text-sm">
            📌 The{" "}
            <span className="font-semibold text-red-700">
              {topGroup.ageGroup}
            </span>{" "}
            group holds the highest share of {topGroup.allPercentage.toFixed(2)}
            %, while{" "}
            <span className="font-semibold text-yellow-700">
              {lowGroup.ageGroup}
            </span>{" "}
            records the lowest at {lowGroup.allPercentage.toFixed(2)}%.
          </p>
        </div>
      </div>

      {/* PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
